import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Typography, Button, Dropdown, Tooltip, Mentions } from 'antd';
import { LockOutlined, ExclamationCircleOutlined, PaperClipOutlined } from '@ant-design/icons';
import session from '../../utils/session';
import templateResources from '../../reducers/templates/resources';
import { useFeature, LockedControl } from '../common/FeatureGate';
import constants from '../../constants';
import './InterviewMessageInput.css';

const { Text } = Typography;

let cachedTemplates = null;
let templatesRequest = null;

const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const loadTemplates = async () => {
    if (Array.isArray(cachedTemplates)) {
        return cachedTemplates;
    }

    if (!templatesRequest) {
        templatesRequest = templateResources.getTemplates()
            .then((response) => {
                const templates = Array.isArray(response?.data) ? response.data : [];
                cachedTemplates = templates;
                return templates;
            })
            .catch(() => [])
            .finally(() => {
                templatesRequest = null;
            });
    }

    return templatesRequest;
};

const parseTemplatePreview = (message = '') => {
    return String(message)
        .split('\n')
        .map((line, index) => (
            <span key={`${index}-${line}`}>
                {line}
                <br />
            </span>
        ));
};

const InterviewMessageInput = ({
    value,
    onChange,
    rows = 3,
    disabled = false,
    placeholder = '',
    showAppend = false,
    appendContent = constants.interviewInviteAppend,
    showTemplates = true,
    templates = null,
    errorMessage = null
}) => {
    const mentionsRef = useRef(null);
    const [loadedTemplates, setLoadedTemplates] = useState([]);
    const userData = session.get('userData') || {};

    // Feature-RBAC: this component owns the Template picker for the broadcast
    // composer, interview invites, module-group preview and sequence editing —
    // gating here covers all of them.
    const canUseTemplates = useFeature('templates.use');
    const templatesEnabled = showTemplates && canUseTemplates;
    
    // Determine dynamic values for mentions
    const recruiterName = userData.name || '{{recruiter}}';
    const brandName = userData.companyName || '{{brand}}';

    const mentionOptions = [
        { value: '{{firstname}}', label: 'First Name', key: 'firstname' },
        { value: '{{lastname}}', label: 'Last Name', key: 'lastname' },
        { value: recruiterName, label: recruiterName, key: 'recruiter' },
        { value: brandName, label: brandName, key: 'brand' }
    ];

    const menuItems = mentionOptions.map(opt => ({ key: opt.value, label: opt.label }));

    // Determine the length of the appended content
    const appendLength = showAppend ? appendContent.length : 0;
    
    // Determine the maximum length available for the editable text
    const maxEditableLength = constants.maxMessageLength - appendLength;
    
    // Current total length
    const currentTotalLength = (value ? value.length : 0) + appendLength;
    
    // Handler for text changes
    const handleChange = (newValue) => {
        let cleanValue = newValue;
        
        // Strip out the @ if it immediately precedes any of our mention values
        mentionOptions.forEach(opt => {
             const pattern = new RegExp(`@${escapeRegExp(opt.value)}`, 'g');
             cleanValue = cleanValue.replace(pattern, opt.value);
        });

        if (cleanValue.length <= maxEditableLength) {
            // Create a synthetic event to pass to onChange
            const event = {
                target: {
                    value: cleanValue
                }
            };
            onChange(event);
        }
    };

    useEffect(() => {
        let isMounted = true;

        if (!templatesEnabled || Array.isArray(templates)) {
            return undefined;
        }

        loadTemplates().then((data) => {
            if (isMounted) {
                setLoadedTemplates(data);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [templatesEnabled, templates]);

    const availableTemplates = useMemo(() => {
        if (!templatesEnabled) {
            return [];
        }

        if (Array.isArray(templates)) {
            return templates;
        }

        return loadedTemplates;
    }, [templatesEnabled, templates, loadedTemplates]);

    const replaceTemplateMergeFields = (templateText = '') => {
        let message = String(templateText);
        message = message.split('{{brand}}').join(brandName);
        message = message.split('{{recruiter}}').join(recruiterName);
        return message;
    };

    const templateMenuItems = (() => {
        if (!availableTemplates.length) {
            return [];
        }

        const groupedTemplates = availableTemplates.reduce((acc, rawTemplateData) => {
            const categoryName = (rawTemplateData?.category || 'Uncategorized').toString().trim() || 'Uncategorized';
            if (!acc[categoryName]) {
                acc[categoryName] = [];
            }
            acc[categoryName].push(rawTemplateData);
            return acc;
        }, {});

        const sortedCategories = Object.keys(groupedTemplates)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

        return sortedCategories.map((category) => {
            const sortedTemplates = [...groupedTemplates[category]]
                .sort((a, b) => (a?.name || '').toLowerCase().localeCompare((b?.name || '').toLowerCase()));

            return {
                key: `category-${category}`,
                label: category,
                children: sortedTemplates.map((rawTemplateData) => {
                    const templateMessage = replaceTemplateMergeFields(rawTemplateData?.details || '');

                    return {
                        key: `template-${rawTemplateData.id}`,
                        label: (
                            <Tooltip
                                title={parseTemplatePreview(templateMessage)}
                                placement="right"
                                color="#39b54a"
                                styles={{ body: { color: 'white' } }}
                            >
                                <div
                                    className="message-template-menu"
                                    style={{ maxWidth: '220px' }}
                                >
                                    <Text ellipsis style={{ fontSize: 'smaller' }}>
                                        {rawTemplateData.name}
                                    </Text>
                                </div>
                            </Tooltip>
                        ),
                        onClick: () => handleChange(templateMessage)
                    };
                })
            };
        });
    })();

    // Mail Merge Logic for the Button
    const handleMenuClick = (e) => {
        const fieldToInsert = e.key;
        const safeValue = value || '';
        
        // Try to access the underlying textarea to insert at cursor
        let textarea = null;
        if (mentionsRef.current) {
            // Attempt to find textarea inside the Mentions component
            // Antd Mentions renders a wrapper div, then the textarea
            // We can try standard DOM traversal if the ref doesn't expose it directly
            const element = mentionsRef.current.nativeElement || (mentionsRef.current.textAreaRef?.current) || (mentionsRef.current.textarea);
            if (element && element.tagName === 'TEXTAREA') {
                textarea = element;
            } else if (mentionsRef.current.wrapper) { // Sometimes exposed as wrapper
                 textarea = mentionsRef.current.wrapper.querySelector('textarea');
            } else {
                 // Fallback: finding via DOM if ref doesn't expose structure cleanly
                 // This depends on how the ref is attached. Antd Mentions ref usually points to the component instance.
                 // let's try a safer fallback:
                 if (typeof globalThis !== 'undefined' && globalThis.document) {
                     textarea = globalThis.document.querySelector('.interview-message-input textarea');
                 }
            }
        }

        if (textarea) {
            const startPos = textarea.selectionStart;
            const endPos = textarea.selectionEnd;
            const textBefore = safeValue.substring(0, startPos);
            const textAfter = safeValue.substring(endPos, safeValue.length);
            
            const newValue = textBefore + fieldToInsert + textAfter;
            
            if (newValue.length <= maxEditableLength) {
                handleChange(newValue);
                
                // Restore focus and cursor position
                const restoreCursor = () => {
                    textarea.focus();
                    const newCursorPos = startPos + fieldToInsert.length;
                    textarea.setSelectionRange(newCursorPos, newCursorPos);
                };

                if (typeof globalThis !== 'undefined' && typeof globalThis.setTimeout === 'function') {
                    globalThis.setTimeout(restoreCursor, 0);
                } else {
                    restoreCursor();
                }
            }
        } else {
            // Fallback: append to end
             const newValue = safeValue + fieldToInsert;
             if (newValue.length <= maxEditableLength) {
                 handleChange(newValue);
             }
        }
    };
    
    const filterOption = (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase());

    return (
        <div className="interview-message-wrapper">
            <div
                className={`interview-message-input-container ${disabled ? 'disabled' : ''}`}
                style={errorMessage ? { borderColor: '#ff4d4f', boxShadow: '0 0 0 2px rgba(255, 77, 79, 0.1)' } : undefined}
            >
                <Mentions
                    ref={mentionsRef}
                    value={value}
                    onChange={handleChange}
                    rows={rows}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="interview-message-input"
                    maxLength={maxEditableLength}
                    style={{ width: '100%', border: 'none', boxShadow: 'none' }}
                    prefix={['@']}
                    options={mentionOptions}
                    filterOption={filterOption}
                    autoSize={{ minRows: 1 }}
                    status={errorMessage ? 'error' : undefined}
                />

                {showAppend && (
                    <div className="message-append-content" style={{ padding: '12px 12px 4px', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <Tooltip title="This note is included automatically">
                            <LockOutlined style={{ color: '#ff4d4f', fontSize: 14, cursor: 'pointer', marginTop: 3, flexShrink: 0 }} />
                        </Tooltip>
                        <Text style={{ color: '#374151', fontSize: 13 }}>
                            <span style={{ whiteSpace: 'pre-wrap' }}>{appendContent.trim()}</span>
                        </Text>
                    </div>
                )}

                <div className="message-footer-bar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        
                        <Text type="secondary" style={{ fontSize: 11, color: '#8c8c8c' }}>
                            Use a
                        </Text>
                        {showTemplates && !canUseTemplates ? (
                            // Shown-but-locked: the wrapper catches the click
                            // because antd disables pointer events on the Button.
                            <LockedControl featureKey="templates.use">
                                <Button
                                    size="small"
                                    icon={<LockOutlined />}
                                    className="template-btn"
                                    disabled
                                >
                                    Template
                                </Button>
                            </LockedControl>
                        ) : (
                            <Dropdown
                                menu={{
                                    items: templateMenuItems,
                                    mode: 'inline'
                                }}
                                trigger={['click']}
                                disabled={disabled || !templateMenuItems.length}
                                overlayClassName="inboxV2-templates-menu"
                            >
                                <Button
                                    size="small"
                                    icon={<PaperClipOutlined />}
                                    className="template-btn"
                                >
                                    Template
                                </Button>
                            </Dropdown>
                        )}
                        <Text type="secondary" style={{ fontSize: 11, color: '#8c8c8c' }}>
                            or type '@' for merge fields or click 
                        </Text>
                         <Dropdown 
                            menu={{ 
                                items: menuItems, 
                                onClick: handleMenuClick 
                            }} 
                            trigger={['click']}
                            disabled={disabled}
                        >
                            <Button 
                                size="small" 
                                icon={<span style={{ fontWeight: 'bold' }}>@</span>}
                                className="merge-field-btn"
                            >
                                Merge Field
                            </Button>
                        </Dropdown>
                    </div>

                    <div className="message-input-counter">
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 12,
                                color: currentTotalLength >= constants.maxMessageLength ? '#ff4d4f' : '#8c8c8c'
                            }}
                        >
                            {currentTotalLength >= constants.maxMessageLength && <ExclamationCircleOutlined style={{ marginRight: 4 }} />}
                            {currentTotalLength} / {constants.maxMessageLength} characters
                        </Text>
                    </div>
                </div>
            </div>
            {errorMessage && (
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 12 }} />
                    <Text style={{ color: '#ff4d4f', fontSize: 12 }}>{errorMessage}</Text>
                </div>
            )}
        </div>
    );
};

export default InterviewMessageInput;
